<template>
  <v-dialog v-model="visible" persistent @keydown.enter="handleOnYes">
    <v-card class="elevation-4 mx-auto" style="width: 100%; max-width: 500px" rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <component v-if="computedIcon" :is="computedIcon" class="title-icon me-2" :style="{ color: computedTitleColor }" />
          <span :style="{ color: computedTitleColor }">{{ title }}</span>
        </div>
        <ButtonBase iconStyle iconType="cancel" color="grey" :disabled="loading" @click="handleOnCancel" />
      </v-card-title>

      <v-card-text class="pa-6 pt-0">
        <p v-html="content"></p>
        <!-- Zusatzinhalte des Aufrufers, z. B. Optionen unterhalb des Textes. -->
        <slot />
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="d-flex justify-center justify-sm-end ma-2">
        <ButtonBase v-if="hasCancelButton" :buttonText="cancelButtonText" :iconType="cancelButtonIconType" :disabled="loading" @click="handleOnCancel" />
        <ButtonBase :buttonText="noButtonText" :iconType="noButtonIconType" :disabled="this.loading" @click="handleOnNo" />
        <ButtonBase
          :buttonText="yesButtonText"
          :iconType="yesButtonIconType"
          :loading="this.loading"
          variant="flat"
          :color="computedYesButtonColor"
          @click="handleOnYes"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ButtonBase from "@/components/buttons/ButtonBase.vue";
import { Info, AlertTriangle, AlertCircle, Check, Trash2 } from "lucide-vue-next";
import { getIcon } from "@/components/utils/utils";
import { gtKey } from "@/i18n/gt";

export default {
  name: "YesNoDialog",

  components: {
    ButtonBase,
    Info,
    AlertTriangle,
    AlertCircle,
    Check,
    Trash2,
  },

  props: {
    type: {
      type: String,
      default: "default",
      validator: (value) => ["default", "warning", "delete"].includes(value),
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: Function,
      required: false,
    },
    iconType: {
      type: String,
      default: null,
    },
    titleColor: {
      type: String,
      required: false,
    },
    content: {
      type: String,
      required: true,
    },
    yesButtonText: {
      type: String,
      default: gtKey("Ja"),
    },
    yesButtonIconType: {
      type: String,
      default: "check",
    },
    onYes: {
      type: Function,
      required: true,
    },
    noButtonText: {
      type: String,
      default: gtKey("Nein"),
    },
    noButtonIconType: {
      type: String,
      default: "cancel",
    },
    onNo: {
      type: Function,
      required: false,
    },
    hasCancelButton: {
      type: Boolean,
      default: false,
    },
    cancelButtonText: {
      type: String,
      default: gtKey("Abbrechen"),
    },
    cancelButtonIconType: {
      type: String,
      default: "cancel",
    },
    onCancel: {
      type: Function,
      required: false,
    },
  },

  data() {
    return {
      visible: false,
      loading: false,
    };
  },

  computed: {
    computedTitleColor() {
      const colors = {
        delete: "var(--error-color)",
        warning: "var(--warning-color)",
        default: "var(--primary-color)",
      };
      return this.titleColor || colors[this.type];
    },

    computedIcon() {
      const defaults = {
        delete: Trash2,
        warning: AlertTriangle,
        default: Info,
      };
      return getIcon(this.icon, this.iconType) || defaults[this.type];
    },

    computedYesButtonColor() {
      const colors = {
        delete: "error",
        warning: "warning",
        default: "primary",
      };
      return colors[this.type];
    },
  },

  methods: {
    openDialog() {
      this.visible = true;
    },

    closeDialog() {
      this.visible = false;
    },

    async handleOnYes() {
      this.loading = true;
      try {
        await this.onYes();
      } finally {
        this.loading = false;
        this.closeDialog();
      }
    },

    async handleOnNo() {
      if (this.onNo) {
        this.onNo();
      }
      this.closeDialog();
    },

    handleOnCancel() {
      if (this.onCancel) {
        this.onCancel();
      }
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.title-icon {
  width: 24px;
  height: 24px;
  stroke-width: 2;
}
</style>
